import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Search,
  Filter,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  MoreVertical,
  User,
  FileText,
  IdCard,
  Car,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";
import axiosInstance from "../../../services/server/axios";
import { 
  message, 
  Modal, 
  Button, 
  Tag, 
  Tooltip, 
  Dropdown, 
  Card, 
  Statistic,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Space,
  Table
} from "antd";

const { Search: AntSearch } = Input;
const { Option } = Select;

const AdminVerifications = () => {
  const { t } = useTranslation();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: "",
    document_type: "",
    search: "",
    date_from: "",
    date_to: ""
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  // Charger les vérifications
  const fetchVerifications = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams({
        page: pagination.current,
        per_page: pagination.pageSize,
        ...filters
      });

      const response = await axiosInstance.get(`/admin/identity-verifications?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setVerifications(response.data.verifications.data || response.data.verifications);
        setPagination(prev => ({
          ...prev,
          total: response.data.verifications.total || response.data.verifications.length
        }));
      }
    } catch (error) {
      console.error("Error fetching verifications:", error);
      message.error(t("fetch_error"));
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters, t]);

  // Charger les statistiques
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axiosInstance.get("/admin/identity-verifications/stats", {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  useEffect(() => {
    fetchVerifications();
    fetchStats();
  }, [fetchVerifications]);

  // Gestion des filtres
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      status: "",
      document_type: "",
      search: "",
      date_from: "",
      date_to: ""
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Pagination
  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }));
  };

  // Actions sur les vérifications
  const handleApprove = async (verificationId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axiosInstance.put(
        `/admin/identity-verifications/${verificationId}/status`,
        { status: "verified" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        message.success(t("verification_approved"));
        fetchVerifications();
        fetchStats();
        setActionModalVisible(false);
      }
    } catch (error) {
      console.error("Error approving verification:", error);
      message.error(t("approval_failed"));
    }
  };

  const handleReject = async (verificationId, reason) => {
    if (!reason.trim()) {
      message.error(t("rejection_reason_required"));
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const response = await axiosInstance.put(
        `/admin/identity-verifications/${verificationId}/status`,
        { status: "rejected", rejection_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        message.success(t("verification_rejected"));
        fetchVerifications();
        fetchStats();
        setActionModalVisible(false);
        setRejectionReason("");
      }
    } catch (error) {
      console.error("Error rejecting verification:", error);
      message.error(t("rejection_failed"));
    }
  };

  // Afficher les détails
  const showDetails = async (verification) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axiosInstance.get(
        `/admin/identity-verifications/${verification.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSelectedVerification(response.data.verification);
        setDetailModalVisible(true);
      }
    } catch (error) {
      console.error("Error fetching verification details:", error);
      message.error(t("fetch_details_error"));
    }
  };

  // Télécharger les documents
  const downloadDocuments = async (verificationId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axiosInstance.get(
        `/admin/identity-verifications/${verificationId}/documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Ouvrir les URLs dans de nouveaux onglets
        const { documents } = response.data;
        Object.values(documents).forEach(url => {
          if (url) window.open(url, '_blank');
        });
      }
    } catch (error) {
      console.error("Error downloading documents:", error);
      message.error(t("download_failed"));
    }
  };

  // Statut badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "orange", text: t("pending"), icon: <Clock size={14} /> },
      processing: { color: "blue", text: t("processing"), icon: <RefreshCw size={14} /> },
      verified: { color: "green", text: t("verified"), icon: <CheckCircle size={14} /> },
      rejected: { color: "red", text: t("rejected"), icon: <XCircle size={14} /> }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <Tag color={config.color} className="d-flex align-items-center gap-1">
        {config.icon}
        {config.text}
      </Tag>
    );
  };

  // Icone du type de document
  const getDocumentIcon = (documentType) => {
    const icons = {
      national_id: <IdCard size={16} />,
      passport: <FileText size={16} />,
      driver_license: <Car size={16} />
    };
    return icons[documentType] || <FileText size={16} />;
  };

  // Format de date sans dayjs
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format de date pour les inputs date
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Menu d'actions
  const getActionMenu = (verification) => {
    const items = [
      {
        key: 'view',
        label: t("view_details"),
        icon: <Eye size={14} />,
        onClick: () => showDetails(verification)
      },
    ];

    if (verification.status === 'pending') {
      items.push(
        {
          key: 'approve',
          label: t("approve_verification"),
          icon: <CheckCircle size={14} />,
          onClick: () => {
            setSelectedVerification(verification);
            setActionModalVisible(true);
          }
        },
        {
          key: 'reject',
          label: t("reject_verification"),
          icon: <XCircle size={14} />,
          onClick: () => {
            setSelectedVerification(verification);
            setActionModalVisible(true);
          }
        }
      );
    }

    return items;
  };

  // Colonnes du tableau
  const columns = [
    {
      title: t("user"),
      dataIndex: "user",
      key: "user",
      render: (user, record) => (
        <Space>
          <div 
            style={{ 
              width: '32px', 
              height: '32px', 
              borderRadius: '50%',
              backgroundColor: 'var(--primary-color3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}
          >
            <User size={16} />
          </div>
          <div>
            <div style={{ color: "var(--primary-color)", fontWeight: 'bold' }}>
              {user?.name || 'N/A'}
            </div>
            <div style={{ color: "var(--primary-color)", fontSize: '12px' }}>
              {user?.email || 'N/A'}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: t("document_type"),
      dataIndex: "document_type",
      key: "document_type",
      render: (documentType) => (
        <Space>
          {getDocumentIcon(documentType)}
          <span style={{ color: "var(--primary-color)" }} className="text-capitalize">
            {documentType?.replace('_', ' ') || 'N/A'}
          </span>
        </Space>
      ),
    },
    {
      title: t("submission_date"),
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => (
        <span style={{ color: "var(--primary-color)" }}>
          {formatDate(date)}
        </span>
      ),
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusBadge(status),
    },
    {
      title: t("verified_at"),
      dataIndex: "verified_at",
      key: "verified_at",
      render: (date) => (
        <span style={{ color: "var(--primary-color)" }}>
          {date ? formatDate(date) : '-'}
        </span>
      ),
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionMenu(record) }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Button 
            type="text" 
            icon={<MoreVertical size={16} />}
            style={{ color: "var(--primary-color)" }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ 
      padding: '24px',
      backgroundColor: "var(--background-body)",
      minHeight: '100vh',
      color: "var(--primary-color)"
    }}>
      <div className="row">
        <div className="col-12">
          {/* En-tête */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '24px' 
          }}>
            <div>
              <h1 style={{ 
                color: "var(--primary-color)",
                marginBottom: '8px',
                fontSize: '24px',
                fontWeight: 'bold'
              }}>
                {t("verification_management")}
              </h1>
              <p style={{ 
                color: "var(--primary-color)",
                margin: 0,
                opacity: 0.8
              }}>
                {t("manage_identity_verifications")}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button 
                icon={<RefreshCw size={16} />}
                onClick={() => {
                  fetchVerifications();
                  fetchStats();
                }}
                loading={loading}
                style={{ 
                  backgroundColor: "var(--primary-color3)",
                  borderColor: "var(--primary-color3)",
                  color: 'white'
                }}
              >
                {t("refresh")}
              </Button>
            </div>
          </div>

          {/* Statistiques */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={6}>
              <Card 
                style={{ 
                  backgroundColor: "var(--background-body)",
                  border: "1px solid var(--primary-color6)",
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <Statistic
                  title={
                    <span style={{ color: "var(--primary-color)" }}>
                      {t("total_verifications")}
                    </span>
                  }
                  value={stats.total || 0}
                  prefix={<BarChart3 size={20} style={{ color: "var(--primary-color3)" }} />}
                  valueStyle={{ color: "var(--primary-color)" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card 
                style={{ 
                  backgroundColor: "var(--background-body)",
                  border: "1px solid var(--primary-color6)",
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <Statistic
                  title={
                    <span style={{ color: "var(--primary-color)" }}>
                      {t("pending")}
                    </span>
                  }
                  value={stats.pending || 0}
                  prefix={<Clock size={20} style={{ color: '#fa8c16' }} />}
                  valueStyle={{ color: "var(--primary-color)" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card 
                style={{ 
                  backgroundColor: "var(--background-body)",
                  border: "1px solid var(--primary-color6)",
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <Statistic
                  title={
                    <span style={{ color: "var(--primary-color)" }}>
                      {t("verified")}
                    </span>
                  }
                  value={stats.verified || 0}
                  prefix={<CheckCircle size={20} style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: "var(--primary-color)" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card 
                style={{ 
                  backgroundColor: "var(--background-body)",
                  border: "1px solid var(--primary-color6)",
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <Statistic
                  title={
                    <span style={{ color: "var(--primary-color)" }}>
                      {t("rejected")}
                    </span>
                  }
                  value={stats.rejected || 0}
                  prefix={<XCircle size={20} style={{ color: '#f5222d' }} />}
                  valueStyle={{ color: "var(--primary-color)" }}
                />
              </Card>
            </Col>
          </Row>

          {/* Filtres */}
          <Card 
            style={{ 
              backgroundColor: "var(--background-body)",
              border: "1px solid var(--primary-color6)",
              marginBottom: '24px'
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={6}>
                <AntSearch
                  placeholder={t("search_placeholder")}
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onSearch={() => fetchVerifications()}
                  style={{ width: '100%' }}
                />
              </Col>
              
              <Col xs={12} md={4}>
                <Select
                  placeholder={t("status")}
                  value={filters.status || undefined}
                  onChange={(value) => handleFilterChange("status", value)}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="pending">{t("pending")}</Option>
                  <Option value="processing">{t("processing")}</Option>
                  <Option value="verified">{t("verified")}</Option>
                  <Option value="rejected">{t("rejected")}</Option>
                </Select>
              </Col>

              <Col xs={12} md={4}>
                <Select
                  placeholder={t("document_type")}
                  value={filters.document_type || undefined}
                  onChange={(value) => handleFilterChange("document_type", value)}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="national_id">{t("national_id")}</Option>
                  <Option value="passport">{t("passport")}</Option>
                  <Option value="driver_license">{t("driver_license")}</Option>
                </Select>
              </Col>

              <Col xs={12} md={4}>
                <input
                  type="date"
                  placeholder={t("submission_date")}
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange("date_from", e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--primary-color6)',
                    borderRadius: '6px',
                    backgroundColor: "var(--background-body)",
                    color: "var(--primary-color)"
                  }}
                />
              </Col>

              <Col xs={12} md={4}>
                <input
                  type="date"
                  placeholder={t("expiry date")}
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange("date_to", e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--primary-color6)',
                    borderRadius: '6px',
                    backgroundColor: "var(--background-body)",
                    color: "var(--primary-color)"
                  }}
                />
              </Col>

              <Col xs={24} md={2}>
                <Button 
                  onClick={clearFilters} 
                  style={{ width: '100%' }}
                >
                  {t("clear")}
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Tableau */}
          <Card
            style={{ 
              backgroundColor: "var(--background-body)",
              border: "1px solid var(--primary-color6)",
            }}
            bodyStyle={{ padding: 0 }}
          >
            <Table
              columns={columns}
              dataSource={verifications}
              rowKey="id"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                onChange: handlePageChange,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${t("showing")} ${range[0]}-${range[1]} ${t("of")} ${total} ${t("entries")}`,
              }}
              locale={{
                emptyText: t("no_verifications_found")
              }}
            />
          </Card>
        </div>
      </div>

      {/* Modal de détails */}
      <Modal
        title={
          <span style={{ color: "var(--primary-color)" }}>
            {t("verification_details")}
          </span>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button 
            key="close" 
            onClick={() => setDetailModalVisible(false)}
            style={{ 
              backgroundColor: "var(--primary-color3)",
              borderColor: "var(--primary-color3)",
              color: 'white'
            }}
          >
            {t("close")}
          </Button>
        ]}
        width={800}
        styles={{
          body: {
            backgroundColor: "var(--background-body)",
            color: "var(--primary-color)"
          }
        }}
      >
        {selectedVerification && (
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div style={{ color: "var(--primary-color)" }}>
                <h6 style={{ color: "var(--primary-color)", marginBottom: '16px' }}>
                  {t("user_information")}
                </h6>
                <p><strong>{t("name")}:</strong> {selectedVerification.user?.name}</p>
                <p><strong>{t("email")}:</strong> {selectedVerification.user?.email}</p>
                
                <h6 style={{ color: "var(--primary-color)", marginTop: '16px', marginBottom: '16px' }}>
                  {t("verification_information")}
                </h6>
                <p><strong>{t("document_type")}:</strong> {selectedVerification.document_type}</p>
                <p><strong>{t("status")}:</strong> {getStatusBadge(selectedVerification.status)}</p>
                <p><strong>{t("submission_date")}:</strong> {formatDate(selectedVerification.created_at)}</p>
                {selectedVerification.verified_at && (
                  <p><strong>{t("verified_at")}:</strong> {formatDate(selectedVerification.verified_at)}</p>
                )}
                {selectedVerification.rejection_reason && (
                  <p><strong>{t("rejection_reason")}:</strong> {selectedVerification.rejection_reason}</p>
                )}
              </div>
            </Col>
            <Col span={12}>
              <div style={{ color: "var(--primary-color)" }}>
                <h6 style={{ color: "var(--primary-color)", marginBottom: '16px' }}>
                  {t("extracted_data")}
                </h6>
                {selectedVerification.verification_data ? (
                  <div>
                    <p><strong>{t("document_number")}:</strong> {selectedVerification.verification_data.document_number}</p>
                    <p><strong>{t("first_name")}:</strong> {selectedVerification.verification_data.first_name}</p>
                    <p><strong>{t("last_name")}:</strong> {selectedVerification.verification_data.last_name}</p>
                    <p><strong>{t("date_of_birth")}:</strong> {selectedVerification.verification_data.date_of_birth}</p>
                    {selectedVerification.verification_data.expiry_date && (
                      <p><strong>{t("expiry_date")}:</strong> {selectedVerification.verification_data.expiry_date}</p>
                    )}
                  </div>
                ) : (
                  <p style={{ color: "var(--primary-color)", opacity: 0.7 }}>
                    {t("no_data_extracted")}
                  </p>
                )}
              </div>
            </Col>
          </Row>
        )}
      </Modal>

      {/* Modal d'actions */}
      <Modal
        title={
          <span style={{ color: "var(--primary-color)" }}>
            {t("verification_action")}
          </span>
        }
        open={actionModalVisible}
        onCancel={() => {
          setActionModalVisible(false);
          setRejectionReason("");
        }}
        footer={null}
        styles={{
          body: {
            backgroundColor: "var(--background-body)",
            color: "var(--primary-color)"
          }
        }}
      >
        {selectedVerification && (
          <div>
            <p style={{ color: "var(--primary-color)" }}>
              {t("action_confirmation")} <strong>{selectedVerification.user?.name}</strong>?
            </p>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                color: "var(--primary-color)",
                display: 'block',
                marginBottom: '8px'
              }}>
                {t("rejection_reason")} ({t("optional")})
              </label>
              <Input.TextArea
                rows={3}
                placeholder={t("rejection_reason_placeholder")}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                style={{
                  backgroundColor: "var(--background-body)",
                  color: "var(--primary-color)",
                  border: "1px solid var(--primary-color6)"
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button onClick={() => setActionModalVisible(false)}>
                {t("cancel")}
              </Button>
              <Button 
                type="primary" 
                danger
                onClick={() => handleReject(selectedVerification.id, rejectionReason)}
              >
                <XCircle size={16} style={{ marginRight: '8px' }} />
                {t("reject")}
              </Button>
              <Button 
                type="primary" 
                onClick={() => handleApprove(selectedVerification.id)}
                style={{ 
                  backgroundColor: "var(--primary-color3)",
                  borderColor: "var(--primary-color3)"
                }}
              >
                <CheckCircle size={16} style={{ marginRight: '8px' }} />
                {t("approve")}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminVerifications;